import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, Redirect } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getUserFriendlyError } from "@/lib/user-friendly-messages";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { PrivacyPolicyDialog } from "@/components/ui/privacy-policy-dialog";

const loginSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function AuthPage() {
  const { user } = useAuth();
  const [location] = useLocation();
  const { toast } = useToast();
  
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [isResetEmailSent, setIsResetEmailSent] = useState(false);

  // Get tab from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'login';
  const [activeTab, setActiveTab] = useState<'login' | 'forgot-password'>(
    initialTab === 'forgot-password' ? 'forgot-password' : 'login'
  );

  // If already logged in, redirect to the dashboard
  if (user) {
    return <Redirect to="/dashboard" />;
  }

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const forgotPasswordForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (data: LoginFormValues) => {
      return await apiRequest("/api/auth/login", {
        method: "POST",
        body: data,
      });
    },
    onSuccess: (response: any) => {
      toast({
        title: "Welcome back!",
        description: "You have been signed in successfully.",
      });
      
      // Invalidate auth queries to refresh user state
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      
      const loggedInUser = response?.user || response;
      const nextPath = loggedInUser?.isSuperAdmin ? '/admin' : '/dashboard';

      // Force redirect after brief delay for auth state update
      setTimeout(() => {
        window.location.href = nextPath;
      }, 100);
    },
    onError: (error: any) => {
      // Use the server's specific message directly — generic mapping loses the detail
      const description = error?.message || "Invalid email or password";
      toast({
        title: "Sign in failed",
        description,
        variant: "destructive",
      });
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: async (data: ForgotPasswordFormValues) => {
      return await apiRequest("/api/forgot-password", {
        method: "POST",
        body: data,
      });
    },
    onSuccess: () => {
      setIsResetEmailSent(true);
      toast({
        title: "Reset link sent!",
        description: "Check your email for password reset instructions.",
      });
    },
    onError: (error: any) => {
      const errorMessage = getUserFriendlyError(error);
      toast({
        title: "Error",
        description: errorMessage.description || "Failed to send reset email. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onLogin = async (data: LoginFormValues) => {
    loginMutation.mutate(data);
  };

  const onForgotPassword = async (data: ForgotPasswordFormValues) => {
    forgotPasswordMutation.mutate(data);
  };

  const handleGoogleLogin = () => {
    window.location.href = "/api/auth/google";
  };

  const switchToForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    setActiveTab('forgot-password');
    // Update URL without refreshing page
    window.history.pushState(null, '', '/auth?tab=forgot-password');
  };

  const switchToLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    setActiveTab('login');
    setIsResetEmailSent(false);
    // Update URL without refreshing page
    window.history.pushState(null, '', '/auth');
  };

  return (
    <>
      <title>{activeTab === 'login' ? 'Sign In' : 'Reset Password'} - SaaSGama</title>
      
      <div className="h-screen overflow-hidden bg-white">
        {/* Split Layout Container */}
        <div className="flex h-full">
          {/* Left Side - Hero Content (hidden on mobile) */}
          <div className="hidden lg:flex lg:w-3/5 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex-col justify-center px-16 py-12 relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full translate-y-24 -translate-x-24"></div>
            
            <div className="relative z-10 max-w-lg">
              <SaaSGamaLogo size="lg" className="mb-8" />
              
              {activeTab === 'login' ? (
                <>
                  <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Manage Your SaaS Portfolio Like a Pro
                  </h1>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Join 10,000+ companies who trust SaaSGama to optimize their software investments, 
                    track renewals, and reduce SaaS spend by up to 30%
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Reset Your Password
                  </h1>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Enter your email address and we'll send you a secure link to reset your password and regain access to your account.
                  </p>
                </>
              )}
              
              {/* Trust Indicators */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="font-semibold">Enterprise Security</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="font-semibold">GDPR Compliant</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="font-semibold">99.9% Uptime</span>
                </div>
              </div>
              
              {/* Testimonial */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/60 shadow-sm">
                <p className="text-gray-700 italic mb-4 leading-relaxed">"SaaSGama helped us reduce our SaaS spend by 35% in just 3 months. The insights are incredible!"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold ring-2 ring-white">M</div>
                  <div>
                    <p className="font-semibold text-gray-900">Maria Chen</p>
                    <p className="text-sm text-gray-600">CTO, TechStart Inc.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Center Divider */}
          <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
          
          {/* Right Side - Form */}
          <div className="flex-1 lg:w-2/5 flex flex-col justify-center px-6 sm:px-12 lg:px-20 relative">
            {/* Mobile Header */}
            <div className="lg:hidden flex justify-between items-center py-6 border-b border-gray-200 mb-8">
              <SaaSGamaLogo size="lg" />
              <div className="text-base text-gray-600">
                Don't have an account?{" "}
                <a href="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  Create account
                </a>
              </div>
            </div>
            
            {/* Desktop Header */}
            <div className="hidden lg:block absolute top-6 right-6">
              <div className="text-base text-gray-600">
                Don't have an account?{" "}
                <a href="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                  Create account
                </a>
              </div>
            </div>

            <div className="max-w-sm mx-auto w-full">
              {/* Mobile Hero */}
              <div className="lg:hidden text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {activeTab === 'login' ? 'Welcome Back' : 'Reset Password'}
                </h1>
                <p className="text-gray-600">
                  {activeTab === 'login' 
                    ? 'Sign in to access your SaaS dashboard'
                    : 'Enter your email to receive a reset link'
                  }
                </p>
              </div>

              {/* Desktop Form Title */}
              <div className="hidden lg:block text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {activeTab === 'login' ? 'Welcome back' : 'Reset password'}
                </h2>
                <p className="text-gray-600 text-lg">
                  {activeTab === 'login' 
                    ? 'Sign in to your account to continue'
                    : 'Enter your email address below'
                  }
                </p>
              </div>

              {/* Login Form */}
              {activeTab === 'login' && (
                <>
                  <Form {...loginForm}>
                    <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-6">
                      <FormField
                        control={loginForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email address</FormLabel>
                            <FormControl>
                              <Input 
                                type="email" 
                                placeholder="Enter your email" 
                                {...field}
                                className="h-12 border-gray-300 focus:border-blue-500"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={loginForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <Input 
                                type="password" 
                                placeholder="Enter your password" 
                                {...field}
                                className="h-12 border-gray-300 focus:border-blue-500"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      {/* Forgot password link */}
                      <div className="text-center">
                        <button 
                          type="button"
                          onClick={switchToForgotPassword}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                        >
                          Forgot your password?
                        </button>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                        disabled={loginMutation.isPending}
                      >
                        {loginMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          "Sign In"
                        )}
                      </Button>
                    </form>
                  </Form>

                  <div className="mt-6">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="bg-white px-2 text-gray-500">Or continue with</span>
                      </div>
                    </div>
                    
                    <Button
                      type="button"
                      onClick={handleGoogleLogin}
                      variant="outline"
                      className="w-full h-14 mt-6 border-gray-300 hover:bg-gray-50 transition-colors duration-200 rounded-xl shadow-sm"
                    >
                      <FcGoogle className="mr-3 h-6 w-6" />
                      Continue with Google
                    </Button>
                  </div>
                </>
              )}

              {/* Forgot Password Form */}
              {activeTab === 'forgot-password' && (
                <>
                  {!isResetEmailSent ? (
                    <Form {...forgotPasswordForm}>
                      <form onSubmit={forgotPasswordForm.handleSubmit(onForgotPassword)} className="space-y-6">
                        <FormField
                          control={forgotPasswordForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email address</FormLabel>
                              <FormControl>
                                <Input 
                                  type="email" 
                                  placeholder="Enter your account email" 
                                  {...field}
                                  className="h-12 border-gray-300 focus:border-blue-500"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <Button 
                          type="submit" 
                          className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                          disabled={forgotPasswordMutation.isPending}
                        >
                          {forgotPasswordMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending reset link...
                            </>
                          ) : (
                            <>
                              <Mail className="mr-2 h-4 w-4" />
                              Send Reset Link
                            </>
                          )}
                        </Button>

                        <div className="text-center">
                          <button 
                            type="button"
                            onClick={switchToLogin}
                            className="inline-flex items-center text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors duration-200"
                          >
                            <ArrowLeft className="mr-1 h-4 w-4" />
                            Back to sign in
                          </button>
                        </div>
                      </form>
                    </Form>
                  ) : (
                    <div className="text-center space-y-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h3>
                        <p className="text-gray-600 mb-4">
                          We've sent a password reset link to your email address. Click the link in the email to reset your password.
                        </p>
                        <p className="text-sm text-gray-500">
                          Didn't receive the email? Check your spam folder or try again.
                        </p>
                      </div>

                      <div className="space-y-4">
                        <Button 
                          type="button"
                          onClick={() => {
                            setIsResetEmailSent(false);
                            forgotPasswordForm.reset();
                          }}
                          variant="outline"
                          className="w-full h-12 border-gray-300 hover:bg-gray-50 transition-colors duration-200 rounded-xl"
                        >
                          Try different email
                        </Button>
                        
                        <button 
                          type="button"
                          onClick={switchToLogin}
                          className="inline-flex items-center text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors duration-200"
                        >
                          <ArrowLeft className="mr-1 h-4 w-4" />
                          Back to sign in
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Mobile Social Proof - only show for login */}
              {activeTab === 'login' && (
                <div className="lg:hidden mt-8 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center mb-4">
                    <div className="flex justify-center gap-6 text-sm text-gray-600">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">10K+</div>
                        <div>Companies</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">30%</div>
                        <div>Avg Savings</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-3 px-6">
          <div className="text-center">
            <div className="text-xs text-gray-500">
              By signing in, you agree to our{" "}
              <button 
                onClick={() => setShowPrivacyPolicy(true)}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Privacy Policy
              </button>
              {" "}and Terms of Service
            </div>
          </div>
        </div>

        {/* Privacy Policy Dialog */}
        <PrivacyPolicyDialog 
          open={showPrivacyPolicy} 
          onOpenChange={setShowPrivacyPolicy} 
        />

      </div>
    </>
  );
}
